import { Component, HostListener, ElementRef, ViewChild, AfterViewChecked, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';

interface ChatMessage {
  sender: 'bot' | 'user';
  text: string;
  time: string;
}

interface QuickReply {
  label: string;
  icon: string;
}

@Component({
  selector: 'app-ai-chat-widget',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './ai-chat-widget.component.html',
  styleUrl: './ai-chat-widget.component.scss',
})
export class AiChatWidgetComponent implements AfterViewChecked {

  @ViewChild('chatBody') private chatBody?: ElementRef<HTMLElement>;

  private readonly router = inject(Router);

  readonly brandName = environment.appName;

  isOpen = false;
  showGreeting = false;
  isTyping = false;
  inputValue = '';

  private greetingShown = false;
  private shouldScroll = false;

  quickReplies: QuickReply[] = [
    { label: 'Pricing plans', icon: 'bi-tag' },
    { label: 'Device compatibility', icon: 'bi-phone' },
    { label: 'How Jadhu works', icon: 'bi-lightbulb' },
    { label: 'Talk to a human', icon: 'bi-headset' },
  ];

  messages: ChatMessage[] = [
    {
      sender: 'bot',
      text: `Hi there 👋 I'm the ${this.brandName} assistant. Ask me about pricing, device compatibility, or how our sessions work — or tap a suggestion below.`,
      time: this.now(),
    },
  ];

  // ---------------------------------------------------------------
  // Proactive greeting bubble — appears once, after the visitor has
  // scrolled a little way into the page.
  // ---------------------------------------------------------------
  @HostListener('window:scroll')
  onScroll(): void {
    if (this.greetingShown || this.isOpen) return;
    if (window.scrollY > 500) {
      this.greetingShown = true;
      this.showGreeting = true;
    }
  }

  toggleChat(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.showGreeting = false;
      this.shouldScroll = true;
    }
  }

  closeChat(): void {
    this.isOpen = false;
  }

  dismissGreeting(event: Event): void {
    event.stopPropagation();
    this.showGreeting = false;
  }

  // ---------------------------------------------------------------
  // Messaging
  // ---------------------------------------------------------------
  sendMessage(): void {
    const text = this.inputValue.trim();
    if (!text) return;

    this.messages.push({ sender: 'user', text, time: this.now() });
    this.inputValue = '';
    this.shouldScroll = true;

    this.simulateReply(text);
  }

  sendQuickReply(reply: QuickReply): void {
    this.messages.push({ sender: 'user', text: reply.label, time: this.now() });
    this.shouldScroll = true;
    this.simulateReply(reply.label);
  }

  private simulateReply(userText: string): void {
    this.isTyping = true;
    this.shouldScroll = true;

    // NOTE: this is a local canned-response engine so the widget works
    // out of the box with no backend. Swap the body of this method for
    // a call to your AI/agent API (e.g. this.chatService.ask(userText))
    // when one is available — the rest of the component doesn't change.
    setTimeout(() => {
      const reply = this.getCannedReply(userText);
      this.messages.push({ sender: 'bot', text: reply.text, time: this.now() });
      this.isTyping = false;
      this.shouldScroll = true;

      if (reply.route) {
        setTimeout(() => this.router.navigateByUrl(reply.route!), 350);
      }
    }, 900);
  }

  private getCannedReply(raw: string): { text: string; route?: string } {
    const text = raw.toLowerCase();

    if (text.includes('pricing') || text.includes('price') || text.includes('cost') || text.includes('plan')) {
      return {
        text: `Plans start at ₹899/month on the yearly plan, with a free 7-day trial on everything. Taking you to the pricing page now.`,
        route: '/pricing',
      };
    }

    if (text.includes('device') || text.includes('compat') || text.includes('phone') || text.includes('ios') || text.includes('android')) {
      return {
        text: `Jadhu works on Android 10+ and iOS/iPadOS 14+ phones and tablets — not on desktop, laptop, or Amazon devices. Opening the compatibility page for you.`,
        route: '/device-compatibility',
      };
    }

    if (text.includes('how') && (text.includes('work') || text.includes('jadhu'))) {
      return {
        text: `Great question — let me walk you through it on our "How it works" page.`,
        route: '/how-it-works',
      };
    }

    if (text.includes('human') || text.includes('talk') || text.includes('support') || text.includes('help')) {
      return {
        text: `Of course — you can reach our team directly on WhatsApp using the green button, or email support and we'll get back to you shortly.`,
      };
    }

    if (text.includes('clinic') || text.includes('school') || text.includes('therapist')) {
      return {
        text: `For clinics and schools we offer dedicated institutional plans with demo access. Taking you there now.`,
        route: '/for-clinics',
      };
    }

    return {
      text: `Thanks for sharing that! For anything specific, try asking about pricing, device compatibility, or how sessions work — or tap "Talk to a human" and our team will help directly.`,
    };
  }

  private now(): string {
    return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll && this.chatBody) {
      this.chatBody.nativeElement.scrollTop = this.chatBody.nativeElement.scrollHeight;
      this.shouldScroll = false;
    }
  }
}
